import { Fragment, useEffect, useState } from 'react';

import moment from 'moment/moment';
import isEmpty from 'lodash/isEmpty';
import { Loader } from '@thinknet-gmbh/platform-common-ui';
import { EmployeeQuestionnaireStatusesEnum } from '@thinknet-gmbh/platform-common';

import { useRouteQuery } from 'src/common/helpers';
import { removeNullAndUndefined } from 'src/common/helpers/common.helpers';
import { useGetEmployeeQuestionnaireById } from 'src/hooks/employee-questionnaire/get-employee-questionnaire-by-id.hook';
import { usePartialEmployeeQuestionnaireUpdate } from 'src/hooks/employee-questionnaire/partial-employee-questionnaire.hook';

import NotFoundPage from 'src/pages/not-found/not-found.page';
import { QuestionnairePageComponent } from 'src/components/questionnaire-page';
import { QuestionnaireThank } from 'src/components/questionnaire-page/components/questionnaire-thank';

import type { UploadFile } from 'antd/es/upload/interface';
import { IEducationAndSkills, IQuestionnaireFormData } from './questionnaire.interfaces';

import { QuestionnaireLayoutWrapper } from './questionnaire.styles';

const prepareEducationAndSkillsData = (data?: IEducationAndSkills) => {
  if (!data) {
    return data;
  }

  const newData = { ...data };
  const newSkills = [] as {
    name?: string;
    description?: string;
  }[];
  if (data.skills) {
    data.skills.forEach(el => {
      if (el?.name) {
        newSkills.push(el);
      }
    });
  }
  if (isEmpty(newSkills)) {
    delete newData.skills;
  }
  return newData;
};

const getValidFile = (uploadedFile?: {
  file?: UploadFile
}) => {
  if (uploadedFile && uploadedFile?.file?.status !== 'removed') {
    return uploadedFile.file;
  }
};

const QuestionnairePage = () => {
  const [data, setData] = useState<IQuestionnaireFormData>({} as IQuestionnaireFormData);

  const params = useRouteQuery();
  const id = params.get('id') || '';

  const { employeeQuestionnaire, loading } = useGetEmployeeQuestionnaireById(id);
  const partialEmployeeQuestionnaireUpdate = usePartialEmployeeQuestionnaireUpdate({});

  useEffect(() => {
    if (!loading && employeeQuestionnaire?._id) {
      const {
        employeePersonalInfo,
        educationAndSkills,
        paymentAndInsurance,
        emergencyContact,
        finishInformation,
      } = employeeQuestionnaire;

      setData({
        employeePersonalInfo: employeePersonalInfo
          ? {
              ...employeePersonalInfo,
              ...(employeePersonalInfo?.birthday && {
                birthday: moment(employeePersonalInfo.birthday),
              }),
            }
          : {},
        educationAndSkills: educationAndSkills || {},
        finishInformation: finishInformation || {},
        paymentAndInsurance: paymentAndInsurance
          ? {
              ...paymentAndInsurance,
              ...(paymentAndInsurance?.visaExpirationDate && {
                visaExpirationDate: moment(paymentAndInsurance.visaExpirationDate),
              }),
              ...(paymentAndInsurance?.workPermissionExpireDate && {
                workPermissionExpireDate: moment(paymentAndInsurance.workPermissionExpireDate),
              }),
              ...(paymentAndInsurance?.residencePermissionExpireDate && {
                residencePermissionExpireDate: moment(
                  paymentAndInsurance.residencePermissionExpireDate
                ),
              }),
              ...(paymentAndInsurance?.certificateOfStudyExpireDate && {
                certificateOfStudyExpireDate: moment(
                  paymentAndInsurance.certificateOfStudyExpireDate
                ),
              }),
            }
          : {},
        emergencyContact: emergencyContact || {},
      });
    }
  }, [employeeQuestionnaire, loading]);

  const onSubmitData = (values: IQuestionnaireFormData) => {
    const {
      employeePersonalInfo,
      educationAndSkills,
      paymentAndInsurance,
      emergencyContact,
      finishInformation,
      signatureId,
      healthInsuranceCardId,
      personalIdCardFrontId,
      personalIdCardBackId,
    } = values;


    const educationAndSkillsData = prepareEducationAndSkillsData(educationAndSkills);

    const personalData =
      !isEmpty(employeePersonalInfo) &&
      employeePersonalInfo &&
      removeNullAndUndefined(employeePersonalInfo);
    const emergencyData =
      !isEmpty(emergencyContact) && emergencyContact && removeNullAndUndefined(emergencyContact);
    const educationData =
      !isEmpty(educationAndSkillsData) &&
      educationAndSkillsData &&
      removeNullAndUndefined(educationAndSkillsData);
    const paymentData =
      !isEmpty(paymentAndInsurance) &&
      paymentAndInsurance &&
      removeNullAndUndefined(paymentAndInsurance);
    const finishData =
      !isEmpty(finishInformation) &&
      finishInformation &&
      removeNullAndUndefined({
        ...finishInformation,
        ...(finishInformation?.signatureId && { signatureId: finishInformation.signatureId }),
      });

    const data = {
      ...(!isEmpty(personalData) && { employeePersonalInfo: personalData }),
      ...(!isEmpty(emergencyData) && { emergencyContact: emergencyData }),
      ...(!isEmpty(paymentData) && { paymentAndInsurance: paymentData }),
      ...(!isEmpty(educationData) && { educationAndSkills: educationData }),
      ...(!isEmpty(finishData) && { finishInformation: finishData }),
      signatureId: signatureId,
      healthInsuranceCardId: getValidFile(healthInsuranceCardId),
      personalIdCardFrontId: getValidFile(personalIdCardFrontId),
      personalIdCardBackId: getValidFile(personalIdCardBackId),
    };

    const payload = {
      _id: id,
      ...data,
    };

    partialEmployeeQuestionnaireUpdate({
      variables: {
        payload,
      },
    });
  };

  const handleDataSet = (values: IQuestionnaireFormData) => {
    setData(oldValue => ({ ...oldValue, ...values }));
  };

  return (
    <QuestionnaireLayoutWrapper>
      {loading ? (
        <Loader />
      ) : (
        <Fragment>
          {employeeQuestionnaire?._id ? (
            <Fragment>
              {employeeQuestionnaire?.status === EmployeeQuestionnaireStatusesEnum.sent ? (
                <QuestionnairePageComponent
                  questionnaireData={employeeQuestionnaire}
                  data={data}
                  handleDataSet={handleDataSet}
                  onSubmit={onSubmitData}
                />
              ) : (
                <QuestionnaireThank />
              )}
            </Fragment>
          ) : (
            <NotFoundPage />
          )}
        </Fragment>
      )}
    </QuestionnaireLayoutWrapper>
  );
};

export default QuestionnairePage;
